import React from 'react';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import { isQAActive } from '../../data/filters/questions/isQAActive';
import { isQuestionCategorySubscribed } from '../../data/filters/questions/isQuestionCategorySubscribed';
import { isQuestionCategoryVisible } from '../../data/filters/questions/isQuestionCategoryVisible';
import { useEvent } from '../../data/hooks/eventHooks';
import RelativeRedirect from '../elements/RelativeRedirect/RelativeRedirect';
import LoadQAAnswers from '../loaders/LoadQAAnswers';
import LoadQAQuestions from '../loaders/LoadQAQuestions';
import EventQuests from '../screens/EventQuests/EventQuests';
import EventQuestsAnswerAttachment from '../screens/EventQuests/EventQuestsAnswerAttachment';
import EventQuestsCategoriesSelection, { EventQuestsCategoriesSelectionWhileCreatingQuestion } from '../screens/EventQuests/EventQuestsCategoriesSelection';
import EventQuestsCategoryParticipants from '../screens/EventQuests/EventQuestsCategoryParticipants';
import EventQuestsEditQuestion from '../screens/EventQuests/EventQuestsEditQuestion';
import EventQuestsPostQuestion from '../screens/EventQuests/EventQuestsPostQuestion';
import EventQuestsQuestion from '../screens/EventQuests/EventQuestsQuestion';
import EventQuestsQuestionAttachment from '../screens/EventQuests/EventQuestsQuestionAttachment';
import EventQuestsQuestionEditAnswer from '../screens/EventQuests/EventQuestsQuestionEditAnswer';
import EventQuestsQuestionFavorited from '../screens/EventQuests/EventQuestsQuestionFavorited';
import EventQuestsQuestionPostAnswer from '../screens/EventQuests/EventQuestsQuestionPostAnswer';
import InviteToMeetingScreen from '../screens/Meetings/InviteToMeetingScreen';
import ErrorBoundary from '../validate/ErrorBoundary';
import HasData from '../validate/HasData';
import WaitWhileBusy from '../WaitWhileBusy';
import { innerChatRoutes } from './chatRoutes';
import participantsRoutes, { innerParticipantRoutes } from './participantsRoutes';
import { useMarkQuestionsAsRead } from '../../data/hooks/questions/useMarkQuestionsAsRead';

const questionRoutes = (props) => (
    <LoadQAAnswers eventId={props.match.params.eventId} questionId={props.match.params.questionId}>
        <HasData path={`events[${props.match.params.eventId}].questions.indexed[${props.match.params.questionId}]`}>
            <Switch>
                <Redirect from={`${props.match.path}/attached`} to={props.match.path} exact />
                <Route path={`${props.match.path}/attached/:attachmentId`} exact component={EventQuestsQuestionAttachment} />
                <Route path={`${props.match.path}/attached/:answerId/:attachmentId`} exact component={EventQuestsAnswerAttachment} />
                <Route path={`${props.match.path}/edit`} exact component={EventQuestsEditQuestion} />
                <Route path={`${props.match.path}/create`} exact component={EventQuestsQuestionPostAnswer} />
                <Redirect from={`${props.match.path}/participants`} exact to={`${props.match.path}`} />
                <Route path={`${props.match.path}/participants`} component={innerParticipantRoutes} />
                <Route path={`${props.match.path}/favorited`} exact component={EventQuestsQuestionFavorited} />
                <Route path={`${props.match.path}/favorited`} component={innerParticipantRoutes} />
                <Route path={`${props.match.path}/invite`} exact component={InviteToMeetingScreen} />
                <Redirect from={`${props.match.path}/chats`} exact to={`${props.match.path}`} />
                <Route path={`${props.match.path}/chats`} component={innerChatRoutes} />
                <Route path={`${props.match.path}/:answerId`} exact component={EventQuestsQuestionEditAnswer} />
                <Route path={`${props.match.path}`} exact component={EventQuestsQuestion} />
                <Redirect to={`${props.match.path}`} />
            </Switch>
        </HasData>
    </LoadQAAnswers>
);

const qaCategoryRoutes = (props) => {
    const { eventId, qcatId } = useParams(),
        event = useEvent(eventId),
        category = event.question_categories.find((cat) => cat.id == qcatId);

    let fallback = false;
    if (qcatId == 'all' && !event.default_question_category) fallback = true;
    else if (!category && !['all', 'mine'].includes(qcatId)) fallback = true;

    useMarkQuestionsAsRead(eventId);

    if (fallback) {
        return <RelativeRedirect to=".." />;
    }

    return (
        <ErrorBoundary altRoute="../..">
            <Switch>
                <Route path={`${props.match.path}/categories`} exact component={EventQuestsCategoriesSelection} />
                <Route path={`${props.match.path}/create/categories`} exact component={EventQuestsCategoriesSelectionWhileCreatingQuestion} />
                <Route path={`${props.match.path}/create`} exact component={EventQuestsPostQuestion} />
                <Route path={`${props.match.path}/question/:questionId`} component={questionRoutes} />
                <Route path={`${props.match.path}/participants`} exact component={EventQuestsCategoryParticipants} />
                <Route path={`${props.match.path}/participants`} component={participantsRoutes} />
                <Route path={`${props.match.path}`} exact component={EventQuests} />
                <Redirect to={`${props.match.path}`} />
            </Switch>
        </ErrorBoundary>
    );
};

export default (props) => {
    const { eventId } = props.match.params,
        event = useEvent(eventId);

    if (!isQAActive(event)) {
        return <RelativeRedirect to=".." />;
    }

    const defaultCategory = isQuestionCategoryVisible(event.default_question_category) ? 'all' : event.question_categories.find((cat) => isQuestionCategorySubscribed(cat))?.id ?? 'mine';

    if (!defaultCategory) {
        return <RelativeRedirect to=".." />;
    }

    return (
        <LoadQAQuestions eventId={props.match.params.eventId}>
            <WaitWhileBusy>
                <Switch>
                    <Redirect from={`${props.match.path}`} exact to={`${props.match.path}/${defaultCategory}`} />
                    <Route path={`${props.match.path}/:qcatId`} component={qaCategoryRoutes} />
                </Switch>
            </WaitWhileBusy>
        </LoadQAQuestions>
    );
};
